/**
 * Real-time Feed Indicator
 * Shows connection status and new post notifications
 */
import { useState, useEffect } from 'react';
import { Wifi, WifiOff, Bell, X } from 'lucide-react';
import { useWebSocket, WSEventType } from '../contexts/WebSocketContext';
import { Button } from './ui/button';

const RealTimeFeedIndicator = ({ onNewPost }) => {
  const { isConnected, addListener } = useWebSocket();
  const [newPosts, setNewPosts] = useState([]);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Listen for new posts
    const cleanup = addListener(WSEventType.NEW_POST, (data) => {
      setNewPosts(prev => [...prev, data]);
      setShowBanner(true);
    });

    return cleanup;
  }, [addListener]);

  const handleLoadNewPosts = () => {
    if (onNewPost) {
      newPosts.forEach(post => onNewPost(post));
    }
    setNewPosts([]);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
  };

  return (
    <>
      {/* Connection Status (subtle indicator) */}
      <div 
        className={`fixed bottom-20 md:bottom-4 left-4 z-40 flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
          isConnected 
            ? 'bg-emerald-50 text-emerald-600 opacity-60 hover:opacity-100' 
            : 'bg-red-50 text-red-600'
        }`}
        title={isConnected ? 'Real-time updates active' : 'Reconnecting...'}
      >
        {isConnected ? (
          <>
            <Wifi className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Live</span>
          </>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5 animate-pulse" />
            <span className="hidden sm:inline">Connecting...</span>
          </>
        )}
      </div>

      {/* New Posts Banner */}
      {showBanner && newPosts.length > 0 && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-3 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white px-4 py-2.5 rounded-full shadow-lg">
            <Bell className="w-4 h-4" />
            <span className="text-sm font-medium">
              {newPosts.length} new {newPosts.length === 1 ? 'post' : 'posts'}
            </span>
            <Button
              onClick={handleLoadNewPosts}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 h-7 px-3 rounded-full text-xs font-semibold"
            >
              View
            </Button>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RealTimeFeedIndicator;
